import React, { useState, useEffect, useRef, useContext } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCustomAlert } from './CustomAlert';
import { 
    faPaperPlane, 
    faImage, 
    faLink, 
    faReply, 
    faThumbtack,
    faUsers,
    faCog,
    faUserPlus,
    faUserMinus,
    faEllipsisV,
    faPray,
    faTasks,
    faVolumeUp,
    faVolumeMute,
    faComments,
    faTrash,
    faDownload,
    faTimes,
    faPencil,
    faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { timeAgo } from '../utils/functions';
import Header from './header';
import ChatLinkText from './ChatLinkText';
import Footer from './footer';

const MAX_OPEN_DISCUSSIONS = 3;
const CLOSED_PREVIEW_COUNT = 4;
const OPEN_LIMIT_MESSAGE = 'Only 3 discussions can be open at a time. Close one first.';

const GroupChat = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const discussionId = searchParams.get('discussion');
    const messagesEndRef = useRef(null);
    const messagesListRef = useRef(null);
    const jumpTimerRef = useRef(null);
    const fileInputRef = useRef(null);
    const hasInitiallyScrolledRef = useRef(false);
    const messagesRef = useRef([]);
    const loadSeqRef = useRef(0);
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    
    const [group, setGroup] = useState(null);
    const [messages, setMessages] = useState([]);
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [replyTo, setReplyTo] = useState(null);
    const [jumpMessageId, setJumpMessageId] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [showMemberList, setShowMemberList] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [userRole, setUserRole] = useState('member');
    const [isGroupCreator, setIsGroupCreator] = useState(false);
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [messageMenuId, setMessageMenuId] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const [showDepartments, setShowDepartments] = useState(false);
    const [showCreateDepartmentModal, setShowCreateDepartmentModal] = useState(false);
    const [showAssignLeaderModal, setShowAssignLeaderModal] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [showManageMembersModal, setShowManageMembersModal] = useState(false);
    const [departmentMembers, setDepartmentMembers] = useState([]);
    const [loadingDepartmentMembers, setLoadingDepartmentMembers] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [imageDraft, setImageDraft] = useState(null);
    const [imageCaption, setImageCaption] = useState('');
    const [openCount, setOpenCount] = useState(0);
    const [discussionsVisible, setDiscussionsVisible] = useState(false);
    const [discussionLists, setDiscussionLists] = useState({ open: [], pending: [], closed: [], is_admin: false });
    const [showTopicForm, setShowTopicForm] = useState(false);
    const [topicDraft, setTopicDraft] = useState('');
    const [savingTopic, setSavingTopic] = useState(false);
    const [closedListVisible, setClosedListVisible] = useState(false);
    const [closedQuery, setClosedQuery] = useState('');
    const [editingDiscussion, setEditingDiscussion] = useState(null);
    const [editTopicDraft, setEditTopicDraft] = useState('');
    const [savingTitle, setSavingTitle] = useState(false);
    const [activeDiscussion, setActiveDiscussion] = useState(null);
    const [topicExpanded, setTopicExpanded] = useState(false);
    const [topicOverflows, setTopicOverflows] = useState(false);
    const topicTextRef = useRef(null);
    const discussionIdRef = useRef(discussionId);
    discussionIdRef.current = discussionId;

    useEffect(() => {
        if (user && user.user_id && groupId) {
            loadGroupData();
            loadMessages();
            loadMembers();
            loadDepartments();
            loadDiscussions();
            
            // Set up polling for new messages
            const interval = setInterval(() => {
                loadMessages(false);
                loadDiscussions();
            }, 5000);
            
            return () => clearInterval(interval);
        }
    }, [user, groupId, discussionId]);

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (showDropdown && !event.target.closest('.dropdown')) {
                setShowDropdown(false);
            }
            if (!event.target.closest('.message-kebab')) {
                setMessageMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    useEffect(() => {
        hasInitiallyScrolledRef.current = false;
        if (!discussionId) setActiveDiscussion(null);
        setTopicExpanded(false);
        setTopicOverflows(false);
    }, [groupId, discussionId]);

    useEffect(() => {
        const el = topicTextRef.current;
        if (!el || topicExpanded) return undefined;
        const measure = () => setTopicOverflows(el.scrollHeight > el.clientHeight + 1);
        measure();
        const frame = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(frame);
    }, [activeDiscussion?.topic, topicExpanded, activeDiscussion?.id]);

    useEffect(() => {
        if (messages.length > 0 && !hasInitiallyScrolledRef.current) {
            scrollToBottom();
            hasInitiallyScrolledRef.current = true;
        }
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const scrollToQuotedMessage = (targetId) => {
        const root = messagesListRef.current;
        if (targetId == null || !root) return;
        const el = root.querySelector(`[data-message-id="${String(targetId)}"]`);
        if (!el) return;
        const next = root.scrollTop + (el.getBoundingClientRect().top - root.getBoundingClientRect().top) - 12;
        root.scrollTo({ top: Math.max(0, next), behavior: 'smooth' });
        setJumpMessageId(String(targetId));
        if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current);
        jumpTimerRef.current = setTimeout(() => setJumpMessageId(null), 1200);
    };

    const loadGroupData = async () => {
        try {
            // This would be a separate API call to get group details
            // For now, we'll get it from the groups list
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    status: 'active'
                })
            });

            const data = await response.json();
            if (data.success) {
                const currentGroup = data.data.find(g => g.group_id == groupId);
                if (currentGroup) {
                    setGroup(currentGroup);
                    setUserRole(currentGroup.role);
                    setIsGroupCreator(currentGroup.created_by == user.user_id);
                } else {
                    setError('Group not found or access denied');
                }
            }
        } catch (err) {
            setError('Failed to load group data');
            console.error('Error loading group data:', err);
        }
    };


    const loadMessages = async (showLoading = true) => {
        const seq = ++loadSeqRef.current;
        try {
            if (showLoading) setLoading(true);
            
            const response = await fetch(`${backendUrl}/get_group_messages.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id,
                    page: 1,
                    limit: 50,
                    ...(discussionIdRef.current ? { discussion_id: discussionIdRef.current } : {})
                })
            });

            const data = await response.json();
            if (seq !== loadSeqRef.current) return;

            if (data.success) {
                const loadedMessages = data.data.messages || [];
                setMessages(prev => {
                    const keys = new Set(loadedMessages.map(item => String(item.id || item.message_id)));
                    const pending = prev.filter(item => item._justSent && !keys.has(String(item.id || item.message_id)));
                    const next = pending.length ? [...loadedMessages, ...pending] : loadedMessages;
                    messagesRef.current = next;
                    return next;
                });
                setPinnedMessages(data.data.pinned_messages || []);
                if (data.data.discussion) setActiveDiscussion(data.data.discussion);
                setError(null);
            } else if (showLoading && messagesRef.current.length === 0) {
                setError(data.messages || 'Failed to load messages');
            }
        } catch (err) {
            if (showLoading && messagesRef.current.length === 0) {
                setError('Network error. Please try again later.');
                console.error('Error loading messages:', err);
            }
        } finally {
            if (showLoading && seq === loadSeqRef.current) setLoading(false);
        }
    };

    const loadDiscussions = async () => {
        try {
            const response = await fetch(`${backendUrl}/get_group_discussions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ group_id: groupId, user_id: user.user_id })
            });
            const data = await response.json();
            if (data.success && data.data) {
                setDiscussionLists({
                    open: data.data.open || [],
                    pending: data.data.pending || [],
                    closed: data.data.closed || [],
                    is_admin: !!data.data.is_admin
                });
                setOpenCount(Number(data.data.open_count) || 0);
            }
        } catch (err) {
            // Chat still works if the discussion list cannot load.
        }
    };

    const submitTopic = async () => {
        const topic = topicDraft.trim();
        if (topic.length < 3 || savingTopic) return;
        if (openCount >= MAX_OPEN_DISCUSSIONS) {
            showAlert('error', 'Open limit reached', OPEN_LIMIT_MESSAGE);
            return;
        }
        setSavingTopic(true);
        try {
            const response = await fetch(`${backendUrl}/create_group_discussion.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ group_id: groupId, user_id: user.user_id, topic })
            });
            const data = await response.json();
            if (data.success) {
                setTopicDraft('');
                setShowTopicForm(false);
                showAlert('success', 'Topic sent', 'An admin will review it. Your name is not attached.');
                loadDiscussions();
            } else {
                showAlert('error', 'Error', data.messages || 'Could not send the topic');
            }
        } catch (err) {
            showAlert('error', 'Error', 'Could not send the topic');
        } finally {
            setSavingTopic(false);
        }
    };

    const reviewTopic = async (item, action, extra = {}) => {
        try {
            const response = await fetch(`${backendUrl}/process_group_discussion.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ discussion_id: item.id, user_id: user.user_id, action, ...extra })
            });
            const data = await response.json();
            if (data.success) {
                loadDiscussions();
                if ((action === 'close' || action === 'reopen' || action === 'rename') && data.data && String(discussionIdRef.current) === String(item.id)) {
                    setActiveDiscussion((prev) => ({ ...(prev || {}), ...data.data }));
                }
                return data.data;
            }
            showAlert('error', 'Error', data.messages || 'Could not update the topic');
        } catch (err) {
            showAlert('error', 'Error', 'Could not update the topic');
        }
        return null;
    };

    const confirmCloseDiscussion = (item) => {
        showConfirm(
            'Close discussion',
            'People can still read it, but nobody can add messages until an admin opens it again.',
            () => reviewTopic(item, 'close'),
            { type: 'danger', confirmText: 'Close', cancelText: 'Cancel' }
        );
    };

    const closeDiscussionsModals = () => {
        setDiscussionsVisible(false);
        setClosedListVisible(false);
        setClosedQuery('');
    };

    const openDiscussion = (item) => {
        closeDiscussionsModals();
        setSearchParams({ discussion: String(item.id) });
    };

    const atOpenLimit = openCount >= MAX_OPEN_DISCUSSIONS;
    const closedMatches = (item) => (item.topic || '').toLowerCase().includes(closedQuery.trim().toLowerCase());
    const canModerateTopics = discussionLists.is_admin || userRole === 'admin' || isGroupCreator;
    const recentClosed = discussionLists.closed.slice(0, CLOSED_PREVIEW_COUNT);

    const resumeDiscussion = (item) => {
        if (atOpenLimit) {
            showAlert('info', 'Open limit reached', OPEN_LIMIT_MESSAGE);
            return;
        }
        reviewTopic(item, 'reopen');
    };

    const approveTopic = (item) => {
        if (atOpenLimit) {
            showAlert('info', 'Open limit reached', OPEN_LIMIT_MESSAGE);
            return;
        }
        reviewTopic(item, 'approve');
    };

    const startEditTitle = (item) => {
        setEditingDiscussion(item);
        setEditTopicDraft(item.topic || '');
    };

    const saveDiscussionTitle = async () => {
        const topic = editTopicDraft.trim();
        if (!editingDiscussion || topic.length < 3 || savingTitle) return;
        setSavingTitle(true);
        try {
            const updated = await reviewTopic(editingDiscussion, 'rename', { topic });
            if (updated) {
                setEditingDiscussion(null);
                setEditTopicDraft('');
            }
        } finally {
            setSavingTitle(false);
        }
    };

    const discussionAgo = (value) => {
        if (value === null || value === undefined || value === '') return '';
        if (typeof value === 'number' || /^\d+$/.test(String(value))) {
            return timeAgo(Number(value));
        }
        const raw = String(value).trim().replace(' ', 'T');
        const utc = new Date(raw.endsWith('Z') ? raw : `${raw}Z`);
        const local = new Date(raw);
        const now = Date.now();
        const utcIsPast = !isNaN(utc.getTime()) && utc.getTime() <= now + 15000;
        const chosen = utcIsPast ? utc : local;
        if (isNaN(chosen.getTime())) return '';
        return timeAgo(Math.floor(chosen.getTime() / 1000));
    };

    const loadMembers = async () => {
        try {
            setLoadingMembers(true);
            
            const response = await fetch(`${backendUrl}/get_group_members.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id,
                    status: 'active'
                })
            });

            const data = await response.json();
            console.log('Members API response:', data);
            console.log('Request data sent:', {
                group_id: groupId,
                user_id: user.user_id,
                status: 'active'
            });

            if (data.success) {
                // Handle both array and object responses
                let membersData = [];
                if (Array.isArray(data.data)) {
                    membersData = data.data;
                } else if (data.data && typeof data.data === 'object') {
                    // If data is an object, check for common array properties
                    if (data.data.members && Array.isArray(data.data.members)) {
                        membersData = data.data.members;
                    } else if (data.data.active_members && Array.isArray(data.data.active_members)) {
                        membersData = data.data.active_members;
                    } else if (data.data.all_members && Array.isArray(data.data.all_members)) {
                        membersData = data.data.all_members;
                    } else {
                        // Convert object to array if it has numeric keys
                        membersData = Object.values(data.data).filter(item => 
                            item && typeof item === 'object' && item.user_id
                        );
                    }
                }
                
                console.log('Members data:', membersData);
                console.log('Members data type:', typeof data.data);
                console.log('Is array?', Array.isArray(data.data));
                console.log('Raw data object:', data.data);
                console.log('Debug info:', data.debug_info);
                setMembers(membersData);
            } else {
                console.error('Failed to load members:', data.messages);
                setMembers([]); // Set empty array on error
            }
        } catch (err) {
            console.error('Error loading members:', err);
        } finally {
            setLoadingMembers(false);
        }
    };

    const loadDepartments = async () => {
        try {
            setLoadingDepartments(true);
            
            const response = await fetch(`${backendUrl}/get_departments.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();

            if (data.success) {
                // Handle both array and object responses
                let departmentsData = [];
                if (Array.isArray(data.data)) {
                    departmentsData = data.data;
                } else if (data.data && typeof data.data === 'object') {
                    departmentsData = Object.values(data.data).filter(item => 
                        item && typeof item === 'object' && item.department_id
                    );
                }
                
                setDepartments(departmentsData);
            } else {
                console.error('Failed to load departments:', data.messages);
                setDepartments([]);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
            setDepartments([]);
        } finally {
            setLoadingDepartments(false);
        }
    };

    const loadDepartmentMembers = async (departmentId) => {
        try {
            setLoadingDepartmentMembers(true);
            
            const response = await fetch(`${backendUrl}/get_department_members.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: departmentId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();

            if (data.success) {
                setDepartmentMembers(data.data || []);
            } else {
                console.error('Failed to load department members:', data.messages);
                setDepartmentMembers([]);
            }
        } catch (err) {
            console.error('Error loading department members:', err);
            setDepartmentMembers([]);
        } finally {
            setLoadingDepartmentMembers(false);
        }
    };

    const removeGroupMember = async (memberToRemove) => {
        showConfirm(
            'Remove Member',
            `Are you sure you want to remove ${memberToRemove.first_name} ${memberToRemove.last_name} from this group?`,
            () => performRemoveMember(memberToRemove),
            { type: 'danger', confirmText: 'Remove', cancelText: 'Cancel' }
        );
    };

    const performRemoveMember = async (memberToRemove) => {
        
        try {
            setSending(true);
            setError(null);
            
            const response = await fetch(`${backendUrl}/remove_group_member.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id_to_remove: memberToRemove.user_id,
                    removed_by: user.user_id
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Refresh the members list
                loadMembers();
                // Show success message
                setError(null);
                // You could add a success state here if needed
            } else {
                setError(data.messages || 'Failed to remove member');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error removing member:', err);
        } finally {
            setSending(false);
        }
    };

    const saveEditedMessage = async () => {
        if (!editingMessage || !newMessage.trim() || sending) return;
        const textToSave = newMessage.trim();
        setSending(true);
        setError(null);
        try {
            const response = await fetch(`${backendUrl}/edit_group_message.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message_id: editingMessage.id,
                    user_id: user.user_id,
                    message: textToSave,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setMessages(prev => prev.map(item => (
                    item.id == editingMessage.id
                        ? { ...item, message: data.data?.message || textToSave, edited_at: data.data?.edited_at || new Date().toISOString(), link_preview: data.data?.link_preview || null }
                        : item
                )));
                setEditingMessage(null);
                setNewMessage('');
            } else {
                setError(data.messages || 'Could not edit message');
            }
        } catch (err) {
            setError('Could not edit message');
        } finally {
            setSending(false);
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        
        if (activeDiscussion?.status === 'closed') return;
        if (!newMessage.trim() || sending) return;
        if (editingMessage) {
            await saveEditedMessage();
            return;
        }

        await sendMessageToAPI(newMessage.trim(), 'text');
    };

    const sendMessageToAPI = async (message, messageType, cloudinaryUrl = null) => {
        try {
            setSending(true);
            setError(null);

            const response = await fetch(`${backendUrl}/send_group_message.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    sender_id: user.user_id,
                    message: message,
                    message_type: messageType,
                    cloudinary_url: cloudinaryUrl,
                    reply_to: replyTo?.id || null,
                    ...(discussionIdRef.current ? { discussion_id: discussionIdRef.current } : {})
                })
            });

            const data = await response.json();

            if (data.success) {
                const messageId = data.data?.message_id || data.data?.id;
                const optimistic = {
                    id: messageId,
                    message_id: messageId,
                    message: data.data?.message ?? message,
                    message_type: data.data?.message_type || messageType,
                    cloudinary_url: data.data?.cloudinary_url || cloudinaryUrl,
                    link_preview: data.data?.link_preview || null,
                    reply_to: data.data?.reply_to || replyTo?.id || null,
                    reply_to_message: replyTo ? {
                        message: replyTo.message,
                        sender_name: replyTo.sender?.name || 'Unknown',
                    } : null,
                    sender: data.data?.sender || {
                        id: user.user_id,
                        name: user.name || user.username || 'You',
                        username: user.username,
                        photo: user.photo,
                    },
                    created_at: data.data?.created_at || new Date().toISOString().slice(0, 19).replace('T', ' '),
                    _justSent: true,
                };
                setMessages(prev => {
                    const exists = prev.some(item => String(item.id || item.message_id) === String(messageId));
                    const next = exists ? prev : [...prev, optimistic];
                    messagesRef.current = next;
                    return next;
                });
                setNewMessage('');
                setReplyTo(null);
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                loadMessages(false);
                return true;
            }
            setError(data.messages || 'Failed to send message');
            return false;
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error sending message:', err);
            return false;
        } finally {
            setSending(false);
        }
    };

    const stageImage = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError('Image size must be less than 10MB');
            return;
        }
        if (imageDraft?.url) URL.revokeObjectURL(imageDraft.url);
        setImageCaption('');
        setImageDraft({ file, url: URL.createObjectURL(file) });
    };

    const clearImageDraft = () => {
        if (imageDraft?.url) URL.revokeObjectURL(imageDraft.url);
        setImageDraft(null);
        setImageCaption('');
    };

    const handleImageUpload = async (file, caption = '') => {
        if (!file) return;
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setError('Image size must be less than 10MB');
            return;
        }
        
        try {
            setSending(true);
            setError(null);
            
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('image', file);
            formData.append('user_id', user.user_id);
            formData.append('group_id', groupId);
            
            // Upload to Cloudinary via our API
            const response = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                method: 'POST',
                body: formData
            });
            
            const data = await response.json();
            
            if (data.success && data.data && data.data.secure_url) {
                const sent = await sendMessageToAPI(caption, 'image', data.data.secure_url);
                if (sent) clearImageDraft();
            } else {
                setError(data.messages || 'Failed to upload image');
            }
            
        } catch (err) {
            console.error('Error uploading image:', err);
            setError('Failed to upload image. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const handleLeaveGroup = async () => {
        showConfirm(
            'Leave Group',
            'Are you sure you want to leave this group?',
            () => performLeaveGroup(),
            { type: 'warning', confirmText: 'Leave', cancelText: 'Cancel' }
        );
    };

    const performLeaveGroup = async () => {
        
        try {
            setSending(true);
            setError(null);
            
            const response = await fetch(`${backendUrl}/leave_group.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    group_id: groupId
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Redirect to groups list
                navigate('/groups');
            } else {
                setError(data.messages || 'Failed to leave group');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error leaving group:', err);
        } finally {
            setSending(false);
        }
    };

    const handleDeleteMessage = async (messageId) => {
        showConfirm(
            'Delete Message',
            'Are you sure you want to delete this message?',
            () => performDeleteMessage(messageId),
            { type: 'danger', confirmText: 'Delete', cancelText: 'Cancel' }
        );
    };

    const performDeleteMessage = async (messageId) => {
        
        try {
            setSending(true);
            setError(null);
            
            const response = await fetch(`${backendUrl}/delete_group_message.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message_id: messageId,
                    deleted_by: user.user_id
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Reload messages to reflect the deletion
                loadMessages(false);
            } else {
                setError(data.messages || 'Failed to delete message');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error deleting message:', err);
        } finally {
            setSending(false);
        }
    };

    // Format time for display using the existing timeAgo function
    const formatTime = (timestamp) => {
        return timeAgo(timestamp);
    };

    const formatDate = (timestamp) => {
        return timeAgo(timestamp);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error && !group) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column" style={{ height: '100vh', overflow: 'hidden' }}>
            <Header />
            <div className="container-fluid p-0 d-flex flex-column" style={{ marginTop: '80px', height: 'calc(100vh - 140px)', maxWidth: '100%', overflow: 'hidden' }}>
            {/* Chat bar */}
            <div className="bg-white border-bottom p-2 p-md-3 flex-shrink-0" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <h5 className="mb-0" style={{ fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group?.group_name}</h5>
                            <small className="text-muted">
                                {group?.member_count} member{group?.member_count !== 1 ? 's' : ''}
                            </small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center flex-shrink-0 ms-2">
                        {userRole === 'admin' && (
                            <button 
                                className="btn btn-outline-primary me-1 me-md-2 d-none d-sm-inline-flex"
                                onClick={() => setShowInviteModal(true)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.5rem' }}
                            >
                                <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                                <span className="d-none d-md-inline">Add Member</span>
                            </button>
                        )}
                        <button 
                            className="btn btn-outline-secondary me-1 me-md-2"
                            onClick={() => setShowMemberList(!showMemberList)}
                            style={{ fontSize: '0.875rem', padding: '0.25rem 0.5rem' }}
                        >
                            <FontAwesomeIcon icon={faUsers} />
                        </button>
                        <div className="dropdown">
                            <button 
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => setShowDropdown(!showDropdown)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.5rem' }}
                            >
                                <FontAwesomeIcon icon={faEllipsisV} />
                            </button>
                            {showDropdown && (
                                <ul className="dropdown-menu show" style={{ position: 'absolute', top: '100%', right: '0', zIndex: 1000 }}>
                                    <li>
                                        <button 
                                            className="dropdown-item"
                                            onClick={() => {
                                                setShowDropdown(false);
                                                // TODO: Implement prayer requests
                                                showAlert('info', 'Coming Soon', 'Prayer Requests feature coming soon!');
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPray} className="me-2" />
                                            Prayer Requests
                                        </button>
                                    </li>
                                    <li>
                                        <button 
                                            className="dropdown-item"
                                            onClick={() => {
                                                setShowDropdown(false);
                                                // TODO: Implement tasks
                                                showAlert('info', 'Coming Soon', 'Tasks feature coming soon!');
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTasks} className="me-2" />
                                            Tasks
                                        </button>
                                    </li>
                                    {userRole === 'admin' && (
                                        <>
                                            <li>
                                                <button 
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setShowDropdown(false);
                                                        setShowInviteModal(true);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                                    Add Members
                                                </button>
                                            </li>
                                            <li>
                                                <button 
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setShowDropdown(false);
                                                        setShowDepartments(true);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                                                    Departments
                                                </button>
                                            </li>
                                        </>
                                    )}
                                    {userRole === 'admin' && (
                                        <>
                                            <li><hr className="dropdown-divider" /></li>
                                            <li>
                                                <button 
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setShowDropdown(false);
                                                        // TODO: Implement group settings
                                                        showAlert('info', 'Coming Soon', 'Group Settings feature coming soon!');
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faCog} className="me-2" />
                                                    Group Settings
                                                </button>
                                            </li>
                                        </>
                                    )}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-grow-1 d-flex" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', minHeight: 0 }}>
                {/* Messages */}
                <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0, maxWidth: '100%', minHeight: 0 }}>
                    {!activeDiscussion && (
                        <button
                            type="button"
                            className="btn btn-light border-bottom text-start rounded-0"
                            onClick={() => { setClosedListVisible(false); setDiscussionsVisible(true); loadDiscussions(); }}
                        >
                            <strong style={{ color: '#48887b' }}>{openCount} open discussion{openCount === 1 ? '' : 's'}</strong>
                        </button>
                    )}
                    {activeDiscussion && (
                        <div className="p-3 border-bottom flex-shrink-0" style={{ background: '#fff3cd' }}>
                            <div className="d-flex justify-content-between align-items-start gap-3">
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div className="d-flex align-items-start gap-2">
                                        <strong ref={topicTextRef} style={{ display: '-webkit-box', WebkitLineClamp: topicExpanded ? 5 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minWidth: 0, flex: 1 }}>{activeDiscussion.topic}</strong>
                                        {canModerateTopics && (
                                            <button type="button" className="btn btn-sm btn-link p-0 flex-shrink-0" title="Edit title" onClick={() => startEditTitle(activeDiscussion)}>
                                                <FontAwesomeIcon icon={faPencil} />
                                            </button>
                                        )}
                                    </div>
                                    {topicOverflows && (
                                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setTopicExpanded((open) => !open)}>
                                            {topicExpanded ? 'Show less' : 'Read more'}
                                        </button>
                                    )}
                                </div>
                                <button type="button" className="btn btn-sm btn-outline-secondary flex-shrink-0" style={{ background: 'transparent' }} onClick={() => setSearchParams({})}>Exit</button>
                            </div>
                            {(userRole === 'admin' || isGroupCreator) && activeDiscussion.status === 'approved' && (
                                <button type="button" className="btn w-100 mt-2 border-0" style={{ background: '#c62828', color: '#fff', fontWeight: 700 }} onClick={() => confirmCloseDiscussion(activeDiscussion)}>Close this discussion</button>
                            )}
                            {(userRole === 'admin' || isGroupCreator) && activeDiscussion.status === 'closed' && (
                                <button type="button" className="btn w-100 mt-2 border-0" style={{ background: '#4a4a4a', color: '#fff', fontWeight: 700 }} onClick={() => resumeDiscussion(activeDiscussion)}>Resume</button>
                            )}
                        </div>
                    )}

                    {/* Pinned Messages */}
                    {pinnedMessages.length > 0 && !activeDiscussion && (
                        <div className="bg-warning bg-opacity-10 p-3 border-bottom">
                            <h6 className="text-warning mb-2">
                                <FontAwesomeIcon icon={faThumbtack} className="me-1" />
                                Pinned Messages
                            </h6>
                            {pinnedMessages.map((msg) => (
                                <div key={msg.id} className="mb-2 p-2 bg-white rounded border">
                                    <small className="text-muted">
                                        <strong>{msg.sender.name}:</strong> {msg.message}
                                    </small>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Messages List */}
                    <div ref={messagesListRef} className="flex-grow-1 p-2 p-md-3" style={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden', width: '100%', maxWidth: '100%' }}>
                        {messages.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                <FontAwesomeIcon icon={faComments} size="3x" className="mb-3" />
                                <p>No messages yet. Start the conversation!</p>
                            </div>
                        ) : (
                            messages.map((message, index) => {
                                const isOwnMessage = message.sender.id === user.user_id;
                                const showDate = index === 0 || 
                                    formatDate(message.created_at) !== formatDate(messages[index - 1].created_at);
                                
                                return (
                                    <div key={message.id} data-message-id={message.id} style={String(jumpMessageId) === String(message.id) ? { backgroundColor: 'rgba(37, 211, 102, 0.22)', borderRadius: 8 } : undefined}>
                                        {showDate && (
                                            <div className="text-center my-3">
                                                <span className="badge bg-secondary">
                                                    {formatDate(message.created_at)}
                                                </span>
                                            </div>
                                        )}
                                        
                                        <div className={`d-flex mb-3 ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'}`} style={{ width: '100%', maxWidth: '100%' }}>
                                            <div className={`message-bubble ${isOwnMessage ? 'own-message' : 'other-message'}`} style={{ maxWidth: '85%', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                                {message.reply_to_message && (
                                                    <div
                                                        className="reply-preview mb-2 p-2 bg-light rounded"
                                                        role="button"
                                                        onClick={() => scrollToQuotedMessage(message.reply_to)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <small className="text-muted">
                                                            Replying to <strong>{message.reply_to_message.sender_name}</strong>
                                                        </small>
                                                        <div className="small text-truncate">
                                                            {message.reply_to_message.message}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                <div className="d-flex align-items-start">
                                                    {!isOwnMessage && (
                                                        <img 
                                                            src={message.sender.photo || '/avatar.jpg'} 
                                                            alt={message.sender.name}
                                                            className="rounded-circle me-2"
                                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                        />
                                                    )}
                                                    
                                                    <div className="flex-grow-1">
                                                        {!isOwnMessage && (
                                                            <small className="text-muted fw-bold">
                                                                {message.sender.name}
                                                            </small>
                                                        )}
                                                        
                                                        <div className="message-content" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
{message.deleted_at ? (
                                                                <span style={{ fontStyle: 'italic', color: '#8a8a8a' }}>
                                                                    {message.deleted_by_admin ? 'Deleted by admin' : 'Message deleted'}
                                                                </span>
                                                            ) : message.message_type === 'image' && message.cloudinary_url ? (
                                                                <>
                                                                <img
                                                                    src={message.cloudinary_url}
                                                                    alt={message.message || 'Shared image'}
                                                                    className="img-fluid rounded chat-message-image"
                                                                    style={{ width: '60vw', maxWidth: '400px', height: 'auto', cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        setSelectedImage(message.cloudinary_url);
                                                                        setShowImageModal(true);
                                                                    }}
                                                                />
                                                                {message.message ? (
                                                                    <div className="mt-1">
                                                                        <ChatLinkText text={message.message} preview={message.link_preview} />
                                                                    </div>
                                                                ) : null}
                                                                </>
                                                            ) : (
                                                                <ChatLinkText text={message.message} preview={message.link_preview} />
                                                            )}
                                                        </div>
                                                        
                                                        <div className="message-time" style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem', display: 'block' }}>
                                                            <small className="text-muted">
                                                                {formatTime(message.created_at)}
                                                            </small>
                                                            {message.edited_at && !message.deleted_at ? (
                                                                <small style={{ color: '#9a9a9a', marginLeft: 6 }}>edited</small>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    
                                                    {isOwnMessage && (
                                                        <img 
                                                            src={message.sender.photo || '/avatar.jpg'} 
                                                            alt={message.sender.name}
                                                            className="rounded-circle ms-2"
                                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                        />
                                                    )}
                                                </div>
                                                
                                                {!message.deleted_at && <div className="message-actions mt-1 d-flex align-items-center justify-content-end" style={{ width: '100%' }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm p-0 border-0 me-2"
                                                        style={{ background: 'transparent', color: '#667781', boxShadow: 'none' }}
                                                        onClick={() => setReplyTo(message)}
                                                    >
                                                        <FontAwesomeIcon icon={faReply} />
                                                    </button>
                                                    {(isOwnMessage || userRole === 'admin' || isGroupCreator) && (
                                                        <div className="message-kebab" style={{ position: 'relative' }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm p-0 border-0"
                                                                style={{ background: 'transparent', color: '#667781', boxShadow: 'none' }}
                                                                title="Message actions"
                                                                onClick={() => setMessageMenuId(messageMenuId === message.id ? null : message.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faEllipsisV} />
                                                            </button>
                                                            {messageMenuId === message.id && (
                                                                <div className="bg-white border rounded shadow-sm py-1" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20, minWidth: 120 }}>
                                                                    {isOwnMessage && message.message_type !== 'image' && !message.edited_at && (
                                                                        <button
                                                                            type="button"
                                                                            className="dropdown-item"
                                                                            onClick={() => {
                                                                                setMessageMenuId(null);
                                                                                setReplyTo(null);
                                                                                setEditingMessage(message);
                                                                                setNewMessage(message.message || '');
                                                                            }}
                                                                        >
                                                                            Edit
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="dropdown-item text-danger"
                                                                        onClick={() => {
                                                                            setMessageMenuId(null);
                                                                            handleDeleteMessage(message.id);
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {editingMessage && (
                        <div className="border-top p-2 bg-light" style={{ width: '100%', maxWidth: '100%' }}>
                            <div className="d-flex align-items-center">
                                <small className="text-muted me-2">Editing message</small>
                                <button
                                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                                    onClick={() => {
                                        setEditingMessage(null);
                                        setNewMessage('');
                                    }}
                                    style={{ padding: '0.125rem 0.375rem' }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Reply Preview */}
                    {replyTo && !editingMessage && (
                        <div className="border-top p-2 bg-light" style={{ width: '100%', maxWidth: '100%' }}>
                            <div className="d-flex align-items-center">
                                <small className="text-muted me-2" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                    Replying to <strong>{replyTo.sender.name}</strong>
                                </small>
                                <button 
                                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                                    onClick={() => setReplyTo(null)}
                                    style={{ padding: '0.125rem 0.375rem' }}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="small text-truncate" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                {replyTo.message}
                            </div>
                        </div>
                    )}

                    {/* Message Input */}
                    {activeDiscussion?.status === 'closed' ? (
                        <div className="border-top p-3 bg-white text-center flex-shrink-0">
                            <p className="text-muted mb-2">This discussion is closed. You can still read it.</p>
                            <button type="button" className="btn btn-primary" onClick={() => setSearchParams({})}>Exit to general chat</button>
                        </div>
                    ) : (
                    <div className="border-top p-2 p-md-3 bg-white flex-shrink-0" style={{ width: '100%', maxWidth: '100%' }}>
                        {error && (
                            <div className="alert alert-danger alert-sm mb-2" role="alert">
                                {error}
                            </div>
                        )}
                        
                        <form onSubmit={sendMessage} className="d-flex align-items-center" style={{ width: '100%', maxWidth: '100%', gap: '8px' }}>
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm flex-shrink-0"
                                onClick={() => fileInputRef.current?.click()}
                                style={{ 
                                    height: '40px', 
                                    width: '40px',
                                    padding: '0',
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center' 
                                }}
                            >
                                <FontAwesomeIcon icon={faImage} />
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    if (e.target.files[0]) {
                                        stageImage(e.target.files[0]);
                                        e.target.value = '';
                                    }
                                }}
                            />
                            
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <textarea
                                    className="form-control"
                                    rows="1"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder={editingMessage ? "Edit your message..." : "Type your message..."}
                                    style={{ 
                                        resize: 'none', 
                                        height: '40px',
                                        minHeight: '40px', 
                                        maxHeight: '120px', 
                                        width: '100%', 
                                        maxWidth: '100%',
                                        lineHeight: '1.5',
                                        paddingTop: '0.5rem',
                                        paddingBottom: '0.5rem'
                                    }}
                                />
                            </div>
                            
                            <button
                                type="submit"
                                className="btn btn-primary flex-shrink-0"
                                disabled={!newMessage.trim() || sending}
                                style={{ 
                                    height: '40px',
                                    width: '40px',
                                    padding: '0',
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center' 
                                }}
                            >
                                {sending ? (
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                )}
                            </button>
                        </form>
                    </div>
                    )}
                    {discussionsVisible && (
                        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1050 }} onClick={closeDiscussionsModals}>
                            <div className="bg-white p-3 mx-auto mt-4" style={{ maxWidth: 520, maxHeight: '85vh', overflow: 'auto', borderRadius: 12 }} onClick={(e) => e.stopPropagation()}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h5 className="mb-0">Discussions</h5>
                                    <button type="button" className="btn btn-sm btn-light" onClick={closeDiscussionsModals}>Close</button>
                                </div>
                                <p className="text-muted small">Send a topic anonymously. The group can discuss it after an admin approves it.</p>
                                {atOpenLimit ? (
                                    <p className="small mb-3" style={{ color: '#c62828' }}>{OPEN_LIMIT_MESSAGE}</p>
                                ) : (
                                    <button type="button" className="btn btn-primary w-100 mb-3" onClick={() => { setDiscussionsVisible(false); setShowTopicForm(true); }}>Create new topic</button>
                                )}
                                {canModerateTopics && discussionLists.pending.length > 0 && <div className="small text-uppercase text-muted mb-2">Waiting for approval</div>}
                                {canModerateTopics && discussionLists.pending.map((item) => (
                                    <div key={`pending-${item.id}`} className="rounded p-2 mb-2" style={{ border: '1px solid #8a8a8a' }}>
                                        <div className="d-flex align-items-start gap-2">
                                            <div className="fw-semibold" style={{ minWidth: 0, flex: 1 }}>{item.topic}</div>
                                            <button type="button" className="btn btn-link p-0 flex-shrink-0" title="Edit title" onClick={() => startEditTitle(item)}>
                                                <FontAwesomeIcon icon={faPencil} />
                                            </button>
                                        </div>
                                        <div className="small text-muted mb-2">{discussionAgo(item.created_at)}</div>
                                        <button type="button" className="btn btn-sm btn-primary me-2" style={{ padding: '1px 8px', lineHeight: '16px' }} onClick={() => approveTopic(item)}>Approve</button>
                                        <button type="button" className="btn btn-sm btn-light" style={{ padding: '1px 8px', lineHeight: '16px' }} onClick={() => reviewTopic(item, 'reject')}>Reject</button>
                                    </div>
                                ))}
                                <div className="small text-uppercase text-muted mb-2">Open</div>
                                {discussionLists.open.length === 0 && <div className="text-muted small mb-2">No open discussions</div>}
                                {discussionLists.open.map((item) => (
                                    <div key={`open-${item.id}`} className="rounded p-2 mb-2" style={{ border: '1px solid #8a8a8a' }}>
                                        <div className="d-flex align-items-start gap-2">
                                            <button type="button" className="btn btn-light text-start p-0 border-0" style={{ minWidth: 0, flex: 1 }} onClick={() => openDiscussion(item)}>
                                                <div className="fw-semibold">{item.topic}</div>
                                                <div className="small text-muted">{discussionAgo(item.created_at)}</div>
                                            </button>
                                            {canModerateTopics && (
                                                <button type="button" className="btn btn-link p-0 flex-shrink-0" title="Edit title" onClick={() => startEditTitle(item)}>
                                                    <FontAwesomeIcon icon={faPencil} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {discussionLists.closed.length > 0 && <div className="small text-uppercase text-muted mb-2">Closed</div>}
                                {recentClosed.map((item) => (
                                    <div key={`closed-${item.id}`} className="rounded p-2 mb-2" style={{ border: '1px solid #8a8a8a' }}>
                                        <div className="d-flex align-items-start gap-2">
                                            <button type="button" className="btn btn-light text-start p-0 border-0" style={{ minWidth: 0, flex: 1 }} onClick={() => openDiscussion(item)}>
                                                <div className="fw-semibold">{item.topic}</div>
                                                <div className="small text-muted">{discussionAgo(item.closed_at || item.created_at)}</div>
                                            </button>
                                            {canModerateTopics && (
                                                <button type="button" className="btn btn-link p-0 flex-shrink-0" title="Edit title" onClick={() => startEditTitle(item)}>
                                                    <FontAwesomeIcon icon={faPencil} />
                                                </button>
                                            )}
                                        </div>
                                        {canModerateTopics && (
                                            <button type="button" className="btn btn-outline-success mt-1" style={{ padding: '2px 8px', fontSize: 12, lineHeight: '16px' }} onClick={() => resumeDiscussion(item)}>Resume</button>
                                        )}
                                    </div>
                                ))}
                                {discussionLists.closed.length > CLOSED_PREVIEW_COUNT && (
                                    <button type="button" className="btn btn-outline-secondary w-100 mt-1" onClick={() => setClosedListVisible(true)}>See all</button>
                                )}
                            </div>
                        </div>
                    )}
                    {closedListVisible && (
                        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1060 }} onClick={() => setClosedListVisible(false)}>
                            <div className="bg-white p-3 mx-auto mt-4" style={{ maxWidth: 520, maxHeight: '85vh', overflow: 'auto', borderRadius: 12 }} onClick={(e) => e.stopPropagation()}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <button type="button" className="btn btn-link p-0" onClick={() => setClosedListVisible(false)}>
                                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                                        Back to discussions
                                    </button>
                                </div>
                                <h5 className="mb-3">Closed discussions</h5>
                                <input className="form-control mb-3" value={closedQuery} onChange={(e) => setClosedQuery(e.target.value)} placeholder="Search closed discussions" />
                                {discussionLists.closed.filter(closedMatches).length === 0 && (
                                    <div className="text-muted small">No closed discussions match that search.</div>
                                )}
                                {discussionLists.closed.filter(closedMatches).map((item) => (
                                    <div key={`closed-all-${item.id}`} className="rounded p-2 mb-2" style={{ border: '1px solid #8a8a8a' }}>
                                        <div className="d-flex align-items-start gap-2">
                                            <button type="button" className="btn btn-light text-start p-0 border-0" style={{ minWidth: 0, flex: 1 }} onClick={() => openDiscussion(item)}>
                                                <div className="fw-semibold">{item.topic}</div>
                                                <div className="small text-muted">{discussionAgo(item.closed_at || item.created_at)}</div>
                                            </button>
                                            {canModerateTopics && (
                                                <button type="button" className="btn btn-link p-0 flex-shrink-0" title="Edit title" onClick={() => startEditTitle(item)}>
                                                    <FontAwesomeIcon icon={faPencil} />
                                                </button>
                                            )}
                                        </div>
                                        {canModerateTopics && (
                                            <button type="button" className="btn btn-outline-success mt-1" style={{ padding: '2px 8px', fontSize: 12, lineHeight: '16px' }} onClick={() => resumeDiscussion(item)}>Resume</button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {showTopicForm && (
                        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1070 }} onClick={() => setShowTopicForm(false)}>
                            <div className="bg-white p-3 mx-auto mt-4" style={{ maxWidth: 520, borderRadius: 12 }} onClick={(e) => e.stopPropagation()}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h5 className="mb-0">Create new discussion</h5>
                                    <button type="button" className="btn btn-sm btn-light" onClick={() => setShowTopicForm(false)}>Close</button>
                                </div>
                                <p className="text-muted small">Send a topic anonymously. The group can discuss it after an admin approves it.</p>
                                <textarea className="form-control" rows={4} maxLength={200} value={topicDraft} onChange={(e) => setTopicDraft(e.target.value)} placeholder="Topic" style={{ resize: 'vertical', minHeight: 88 }} />
                                <div className="text-end text-muted small mb-2">{topicDraft.length}/200</div>
                                <button type="button" className="btn btn-primary w-100" disabled={topicDraft.trim().length < 3 || savingTopic || atOpenLimit} onClick={submitTopic}>{savingTopic ? 'Sending...' : 'Send topic'}</button>
                            </div>
                        </div>
                    )}
                    {editingDiscussion && (
                        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1080 }} onClick={() => setEditingDiscussion(null)}>
                            <div className="bg-white p-3 mx-auto mt-4" style={{ maxWidth: 520, borderRadius: 12 }} onClick={(e) => e.stopPropagation()}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h5 className="mb-0">Edit discussion title</h5>
                                    <button type="button" className="btn btn-sm btn-light" onClick={() => setEditingDiscussion(null)}>Close</button>
                                </div>
                                <textarea className="form-control" rows={4} maxLength={200} value={editTopicDraft} onChange={(e) => setEditTopicDraft(e.target.value)} placeholder="Title" style={{ resize: 'vertical', minHeight: 88 }} />
                                <div className="text-end text-muted small mb-2">{editTopicDraft.length}/200</div>
                                <button type="button" className="btn btn-primary w-100" disabled={editTopicDraft.trim().length < 3 || savingTitle} onClick={saveDiscussionTitle}>{savingTitle ? 'Saving...' : 'Save title'}</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Member List Sidebar */}
                {showMemberList && (
                    <div className="border-start bg-light d-none d-md-block" style={{ width: '300px', maxWidth: '100%', overflowX: 'hidden' }}>
                        <div className="p-3 border-bottom">
                            <h6 className="mb-0">Group Members</h6>
                        </div>
                        <div className="p-3">
                            {loadingMembers ? (
                                <div className="text-center">
                                    <div className="spinner-border spinner-border-sm" role="status"></div>
                                    <span className="ms-2">Loading members...</span>
                                </div>
                            ) : (
                                <div>
                                    <div className="mb-3">
                                        <small className="text-muted">
                                            {Array.isArray(members) ? members.length : 0} member{Array.isArray(members) && members.length !== 1 ? 's' : ''}
                                        </small>
                                    </div>
                                    
                                    {Array.isArray(members) && members.map((member) => {
                                        const canRemoveMember = userRole === 'admin' && 
                                                               member.user_id !== user.user_id && 
                                                               (isGroupCreator || member.role !== 'admin');
                                        
                                        return (
                                            <div key={member.user_id} className="d-flex align-items-center mb-3">
                                                <div className="me-3">
                                                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                         style={{ width: '40px', height: '40px', fontSize: '14px' }}>
                                                        {member.first_name ? member.first_name.charAt(0).toUpperCase() : '?'}
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold">
                                                        {member.first_name} {member.last_name}
                                                        {member.user_id === group?.created_by && (
                                                            <span className="badge bg-warning ms-2">Creator</span>
                                                        )}
                                                    </div>
                                                    <div className="text-muted small">
                                                        @{member.username}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className={`badge ${
                                                        member.role === 'admin' ? 'bg-danger' : 
                                                        member.role === 'therapist' ? 'bg-success' : 'bg-secondary'
                                                    }`}>
                                                        {member.role}
                                                    </span>
                                                    {canRemoveMember && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeGroupMember(member)}
                                                            disabled={sending}
                                                            title={isGroupCreator ? 'Remove member' : 'Remove member (cannot remove admins)'}
                                                        >
                                                            <FontAwesomeIcon icon={faUserMinus} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {Array.isArray(members) && members.length === 0 && (
                                        <div className="text-center text-muted">
                                            <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                            <p>No members found</p>
                                        </div>
                                    )}
                                    
                                    {/* Leave Group Button */}
                                    {userRole !== 'admin' && (
                                        <div className="mt-4 pt-3 border-top">
                                            <button 
                                                className="btn btn-outline-danger btn-sm w-100"
                                                onClick={handleLeaveGroup}
                                            >
                                                Leave Group
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Departments Sidebar */}
                {showDepartments && (
                    <div className="border-start bg-light" style={{ width: '350px' }}>
                        <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Departments</h6>
                            {userRole === 'admin' && (
                                <button 
                                    className="btn btn-sm btn-primary"
                                    onClick={() => setShowCreateDepartmentModal(true)}
                                >
                                    <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                                    Create
                                </button>
                            )}
                        </div>
                        <div className="p-3">
                            {loadingDepartments ? (
                                <div className="text-center">
                                    <div className="spinner-border spinner-border-sm" role="status"></div>
                                    <span className="ms-2">Loading departments...</span>
                                </div>
                            ) : (
                                <div>
                                    {Array.isArray(departments) && departments.length > 0 ? (
                                        departments.map((department) => (
                                            <div key={department.department_id} className="card mb-3">
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-start">
                                                        {department.logo_url ? (
                                                            <img 
                                                                src={department.logo_url} 
                                                                alt={department.name}
                                                                className="me-3 rounded"
                                                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div className="me-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                                 style={{ width: '40px', height: '40px', fontSize: '14px' }}>
                                                                {department.name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <div className="flex-grow-1">
                                                            <h6 className="card-title mb-1">{department.name}</h6>
                                                            {department.description && (
                                                                <p className="card-text text-muted small mb-2">
                                                                    {department.description.length > 60 
                                                                        ? department.description.substring(0, 60) + '...'
                                                                        : department.description
                                                                    }
                                                                </p>
                                                            )}
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <small className="text-muted">
                                                                    {department.member_count} member{department.member_count !== 1 ? 's' : ''}
                                                                </small>
                                                                {department.leader_first_name && (
                                                                    <small className="text-muted">
                                                                        Leader: {department.leader_first_name} {department.leader_last_name}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {userRole === 'admin' && (
                                                        <div className="mt-2 d-flex gap-2 flex-wrap">
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => {
                                                                    setSelectedDepartment(department);
                                                                    setShowAssignLeaderModal(true);
                                                                }}
                                                            >
                                                                Assign Leader
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-outline-success"
                                                                onClick={() => {
                                                                    setSelectedDepartment(department);
                                                                    loadDepartmentMembers(department.department_id);
                                                                    setShowManageMembersModal(true);
                                                                }}
                                                            >
                                                                Manage Members
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => {
                                                                    // TODO: Implement edit department
                                                                    showAlert('info', 'Coming Soon', 'Edit department feature coming soon!');
                                                                }}
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center text-muted">
                                            <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                            <p>No departments found</p>
                                            {userRole === 'admin' && (
                                                <button 
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => setShowCreateDepartmentModal(true)}
                                                >
                                                    Create First Department
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Add Member Modal */}
            {showInviteModal && (
                <AddMemberModal 
                    groupId={groupId}
                    onClose={() => setShowInviteModal(false)}
                    onMemberAdded={loadMembers}
                />
            )}

            {/* Create Department Modal */}
            {showCreateDepartmentModal && (
                <CreateDepartmentModal 
                    groupId={groupId}
                    onClose={() => setShowCreateDepartmentModal(false)}
                    onDepartmentCreated={loadDepartments}
                />
            )}

            {/* Assign Leader Modal */}
            {showAssignLeaderModal && selectedDepartment && (
                <AssignLeaderModal 
                    department={selectedDepartment}
                    members={members}
                    onClose={() => {
                        setShowAssignLeaderModal(false);
                        setSelectedDepartment(null);
                    }}
                    onLeaderAssigned={loadDepartments}
                />
            )}

            {/* Manage Members Modal */}
            {showManageMembersModal && selectedDepartment && (
                <ManageMembersModal 
                    department={selectedDepartment}
                    groupMembers={members}
                    departmentMembers={departmentMembers}
                    loading={loadingDepartmentMembers}
                    onClose={() => {
                        setShowManageMembersModal(false);
                        setSelectedDepartment(null);
                        setDepartmentMembers([]);
                    }}
                    onMembersUpdated={() => {
                        loadDepartmentMembers(selectedDepartment.department_id);
                        loadDepartments();
                    }}
                />
            )}
            
            {/* Fullscreen Image Modal */}
            {showImageModal && selectedImage && (
                <div 
                    className="modal show d-block" 
                    style={{ 
                        backgroundColor: 'rgba(0,0,0,0.95)', 
                        zIndex: 9999,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => {
                        setShowImageModal(false);
                        setSelectedImage(null);
                    }}
                >
                    <div 
                        style={{ 
                            position: 'relative',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            className="btn btn-light btn-sm"
                            style={{
                                position: 'absolute',
                                top: '-50px',
                                right: '0',
                                zIndex: 10000,
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onClick={() => {
                                setShowImageModal(false);
                                setSelectedImage(null);
                            }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        
                        {/* Image */}
                        <img 
                            src={selectedImage} 
                            alt="Fullscreen view"
                            style={{ 
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }}
                        />
                        
                        {/* Download Button */}
                        <a
                            href={selectedImage}
                            download={`group-chat-image-${Date.now()}.jpg`}
                            className="btn btn-primary mt-3"
                            style={{
                                borderRadius: '25px',
                                padding: '10px 20px'
                            }}
                            onClick={(e) => {
                                // Allow download to proceed
                                e.stopPropagation();
                            }}
                        >
                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                            Download Image
                        </a>
                    </div>
                </div>
            )}
            
            {/* Custom Alert Component */}
            <AlertComponent />
            
            {/* Responsive Styles */}
            <style>{`
                .message-bubble {
                    padding: 10px 15px;
                    border-radius: 18px;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    max-width: 85%;
                }
                
                @media (max-width: 768px) {
                    .message-bubble {
                        max-width: 90%;
                        padding: 8px 12px;
                    }
                }
                
                .own-message {
                    background-color: #dcf8c6;
                    color: #000000;
                    border-top-right-radius: 4px;
                }
                
                .other-message {
                    background-color: #ffffff;
                    color: #000000;
                    border-top-left-radius: 4px;
                }
                
                .message-content p {
                    margin-bottom: 0.25rem;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    white-space: pre-wrap;
                    max-width: 100%;
                }
                
                .container-fluid {
                    overflow-x: hidden !important;
                }
                
                /* Prevent horizontal scroll on mobile */
                @media (max-width: 576px) {
                    .container-fluid {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                    
                    .message-bubble {
                        max-width: 85%;
                        padding: 8px 12px;
                    }
                    
                    /* Make header responsive */
                    .bg-white.border-bottom {
                        padding: 0.75rem !important;
                    }
                    
                    /* Responsive buttons */
                    .btn {
                        padding: 0.25rem 0.5rem;
                        font-size: 0.875rem;
                    }
                    
                    /* Responsive text */
                    h5 {
                        font-size: 1rem !important;
                    }
                    
                    /* Chat message images: 60% view width, max 400px, height auto */
                    .message-content img.chat-message-image,
                    .message-content .chat-message-image {
                        width: 60vw !important;
                        max-width: 400px !important;
                        height: auto !important;
                    }
                }
                
                /* Tablet adjustments */
                @media (max-width: 768px) {
                    .message-bubble {
                        max-width: 80%;
                    }
                }
            `}</style>
        </div>
        {imageDraft && (
            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column" style={{ background: '#111', zIndex: 1080 }}>
                <div className="d-flex justify-content-between align-items-center p-2 bg-white">
                    <button type="button" className="btn btn-sm btn-light" onClick={clearImageDraft} disabled={sending}>Close</button>
                    <strong>Send photo</strong>
                    <span style={{ width: 52 }} />
                </div>
                <div className="flex-grow-1 d-flex align-items-center justify-content-center p-2">
                    <img src={imageDraft.url} alt="Selected" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
                </div>
                <div className="p-2 bg-white d-flex align-items-end gap-2">
                    <textarea
                        className="form-control"
                        rows={2}
                        value={imageCaption}
                        onChange={(e) => setImageCaption(e.target.value)}
                        placeholder="Add a caption"
                    />
                    <button
                        type="button"
                        className="btn btn-primary flex-shrink-0"
                        disabled={sending}
                        onClick={() => handleImageUpload(imageDraft.file, imageCaption.trim())}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </div>
        )}
        <Footer />
    </div>
    );
};

// Add Member Modal Component
const AddMemberModal = ({ groupId, onClose, onMemberAdded }) => {
    const { user } = useContext(MyContext);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [addingUsers, setAddingUsers] = useState(new Set());

    // Debounced search
    useEffect(() => {
        if (searchQuery.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        const timeoutId = setTimeout(async () => {
            await searchUsers(searchQuery.trim());
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const searchUsers = async (query) => {
        try {
            setSearching(true);
            setError(null);

            const response = await fetch(`${backendUrl}/search_users.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    search_query: query,
                    group_id: groupId,
                    current_user_id: user.user_id
                })
            });

            const data = await response.json();

            if (data.success) {
                setSearchResults(data.data || []);
            } else {
                setError(data.messages || 'Failed to search users');
                setSearchResults([]);
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error searching users:', err);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    const addUserToGroup = async (userToAdd) => {
        try {
            setAddingUsers(prev => new Set(prev).add(userToAdd.user_id));
            setError(null);

            const response = await fetch(`${backendUrl}/invite_to_group.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    invited_by: user.user_id,
                    invitation_type: 'username',
                    invitation_value: userToAdd.username
                })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(`${userToAdd.username} has been added successfully`);
                // Remove from search results
                setSearchResults(prev => prev.filter(u => u.user_id !== userToAdd.user_id));
                // Call callback to refresh members list
                if (onMemberAdded) {
                    onMemberAdded();
                }
            } else {
                setError(data.messages || `Failed to add ${userToAdd.username}`);
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error adding user:', err);
        } finally {
            setAddingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(userToAdd.user_id);
                return newSet;
            });
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Members</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        
                        {success && (
                            <div className="alert alert-success" role="alert">
                                {success}
                            </div>
                        )}

                        <div className="mb-3">
                            <label htmlFor="searchInput" className="form-label">Search by username</label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="searchInput"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Type username to search..."
                                    autoComplete="off"
                                />
                                {searching && (
                                    <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                        <div className="spinner-border spinner-border-sm text-muted" role="status">
                                            <span className="visually-hidden">Searching...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="form-text">
                                Start typing a username to find users to add to the group
                            </div>
                        </div>

                        {/* Search Results */}
                        {searchQuery.trim().length >= 2 && (
                            <div className="search-results">
                                <h6 className="text-muted mb-3">Search Results</h6>
                                {searchResults.length > 0 ? (
                                    <div className="list-group">
                                        {searchResults.map((searchUser) => (
                                            <div key={searchUser.user_id} className="list-group-item">
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        {searchUser.photo_url ? (
                                                            <img 
                                                                src={searchUser.photo_url} 
                                                                alt={searchUser.display_name}
                                                                className="rounded-circle"
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                                 style={{ width: '50px', height: '50px', fontSize: '18px' }}>
                                                                {searchUser.first_name ? searchUser.first_name.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="fw-bold">{searchUser.display_name}</div>
                                                        <div className="text-muted">@{searchUser.username}</div>
                                                    </div>
                                                    <div>
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => addUserToGroup(searchUser)}
                                                            disabled={addingUsers.has(searchUser.user_id)}
                                                        >
                                                            {addingUsers.has(searchUser.user_id) ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                                    Adding...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                                                                    Add
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-4">
                                        <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                        <p>No users found matching "{searchQuery}"</p>
                                        <small>Try a different username</small>
                                    </div>
                                )}
                            </div>
                        )}

                        {searchQuery.trim().length < 2 && (
                            <div className="text-center text-muted py-4">
                                <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                <p>Start typing a username to search for users</p>
                                <small>Minimum 2 characters required</small>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Create Department Modal Component
const CreateDepartmentModal = ({ groupId, onClose, onDepartmentCreated }) => {
    const { user } = useContext(MyContext);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        logo_url: ''
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            setError('Department name is required');
            return;
        }
        
        if (formData.name.length < 3 || formData.name.length > 255) {
            setError('Department name must be between 3 and 255 characters');
            return;
        }
        
        if (formData.description && (formData.description.length < 10 || formData.description.length > 1000)) {
            setError('Description must be between 10 and 1000 characters');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let logoUrl = formData.logo_url.trim();
            
            // Upload logo file to Cloudinary if provided
            if (logoFile) {
                const formData = new FormData();
                formData.append('file', logoFile);
                formData.append('user_id', user.user_id);
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: formData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    logoUrl = uploadData.data.secure_url;
                } else {
                    setError(uploadData.messages || 'Failed to upload logo');
                    return;
                }
            }

            const response = await fetch(`${backendUrl}/create_department.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                    logo_url: logoUrl,
                    created_by: user.user_id
                })
            });

            const data = await response.json();

            if (data.success) {
                onDepartmentCreated();
                onClose();
            } else {
                setError(data.messages || 'Failed to create department');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error creating department:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Create Department</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            
                            <div className="mb-3">
                                <label htmlFor="departmentName" className="form-label">Department Name *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="departmentName"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="Enter department name"
                                    required
                                    minLength={3}
                                    maxLength={255}
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label htmlFor="departmentDescription" className="form-label">Description</label>
                                <textarea
                                    className="form-control"
                                    id="departmentDescription"
                                    rows="3"
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder="Describe the department's purpose and responsibilities"
                                    minLength={10}
                                    maxLength={1000}
                                />
                                <div className="form-text">
                                    {formData.description.length}/1000 characters
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label htmlFor="departmentLogo" className="form-label">Department Logo</label>
                                
                                {/* File Upload */}
                                <div className="mb-2">
                                    <input
                                        type="file"
                                        className="form-control"
                                        id="logoFile"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setLogoFile(file);
                                                const reader = new FileReader();
                                                reader.onload = (e) => setLogoPreview(e.target.result);
                                                reader.readAsDataURL(file);
                                                // Clear URL input when file is selected
                                                setFormData({...formData, logo_url: ''});
                                            }
                                        }}
                                    />
                                    <div className="form-text">
                                        Upload an image file (JPG, PNG, GIF) - Max 10MB
                                    </div>
                                </div>
                                
                                {/* Logo Preview */}
                                {logoPreview && (
                                    <div className="mb-2">
                                        <img 
                                            src={logoPreview} 
                                            alt="Logo preview" 
                                            className="img-thumbnail"
                                            style={{ maxWidth: '100px', maxHeight: '100px' }}
                                        />
                                        <button 
                                            type="button"
                                            className="btn btn-sm btn-outline-danger ms-2"
                                            onClick={() => {
                                                setLogoFile(null);
                                                setLogoPreview(null);
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                                
                                {/* OR Divider */}
                                <div className="text-center text-muted mb-2">OR</div>
                                
                                {/* URL Input */}
                                <input
                                    type="url"
                                    className="form-control"
                                    id="departmentLogo"
                                    value={formData.logo_url}
                                    onChange={(e) => {
                                        setFormData({...formData, logo_url: e.target.value});
                                        // Clear file when URL is entered
                                        if (e.target.value) {
                                            setLogoFile(null);
                                            setLogoPreview(null);
                                        }
                                    }}
                                    placeholder="https://example.com/logo.png"
                                />
                                <div className="form-text">
                                    Enter a logo URL instead of uploading a file
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                ) : null}
                                Create Department
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Assign Leader Modal Component
const AssignLeaderModal = ({ department, members, onClose, onLeaderAssigned }) => {
    const { user } = useContext(MyContext);
    const [selectedLeaderId, setSelectedLeaderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedLeaderId) {
            setError('Please select a leader');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${backendUrl}/assign_department_leader.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: department.department_id,
                    leader_id: selectedLeaderId,
                    assigned_by: user.user_id
                })
            });

            const data = await response.json();

            if (data.success) {
                onLeaderAssigned();
                onClose();
            } else {
                setError(data.messages || 'Failed to assign leader');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error assigning leader:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Assign Department Leader</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            
                            <div className="mb-3">
                                <label className="form-label">Department: <strong>{department.name}</strong></label>
                            </div>
                            
                            <div className="mb-3">
                                <label htmlFor="leaderSelect" className="form-label">Select Leader *</label>
                                <select
                                    className="form-select"
                                    id="leaderSelect"
                                    value={selectedLeaderId}
                                    onChange={(e) => setSelectedLeaderId(e.target.value)}
                                    required
                                >
                                    <option value="">Choose a leader...</option>
                                    {Array.isArray(members) && members.map((member) => (
                                        <option key={member.user_id} value={member.user_id}>
                                            {member.first_name} {member.last_name} (@{member.username})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                ) : null}
                                Assign Leader
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Manage Members Modal Component
const ManageMembersModal = ({ department, groupMembers, departmentMembers, loading, onClose, onMembersUpdated }) => {
    const { user } = useContext(MyContext);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [addingUsers, setAddingUsers] = useState(new Set());
    const [removingUsers, setRemovingUsers] = useState(new Set());

    const addMemberToDepartment = async (userToAdd) => {
        try {
            setAddingUsers(prev => new Set(prev).add(userToAdd.user_id));
            setError(null);

            const response = await fetch(`${backendUrl}/add_department_member.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: department.department_id,
                    user_id: userToAdd.user_id,
                    added_by: user.user_id
                })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(`${userToAdd.username} has been added to the department`);
                onMembersUpdated();
            } else {
                setError(data.messages || `Failed to add ${userToAdd.username}`);
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error adding member:', err);
        } finally {
            setAddingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(userToAdd.user_id);
                return newSet;
            });
        }
    };

    const removeMemberFromDepartment = async (userToRemove) => {
        try {
            setRemovingUsers(prev => new Set(prev).add(userToRemove.user_id));
            setError(null);

            const response = await fetch(`${backendUrl}/remove_department_member.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: department.department_id,
                    user_id: userToRemove.user_id,
                    removed_by: user.user_id
                })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(`${userToRemove.username} has been removed from the department`);
                onMembersUpdated();
            } else {
                setError(data.messages || `Failed to remove ${userToRemove.username}`);
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error removing member:', err);
        } finally {
            setRemovingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(userToRemove.user_id);
                return newSet;
            });
        }
    };

    // Get group members who are not in the department
    const availableMembers = Array.isArray(groupMembers) ? groupMembers.filter(member => 
        !departmentMembers.some(deptMember => deptMember.user_id === member.user_id)
    ) : [];

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Manage Department Members</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        
                        {success && (
                            <div className="alert alert-success" role="alert">
                                {success}
                            </div>
                        )}

                        <div className="mb-3">
                            <h6>Department: <strong>{department.name}</strong></h6>
                        </div>

                        <div className="row">
                            {/* Current Department Members */}
                            <div className="col-md-6">
                                <h6 className="text-success">Current Members ({departmentMembers.length})</h6>
                                <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {loading ? (
                                        <div className="text-center">
                                            <div className="spinner-border spinner-border-sm" role="status"></div>
                                            <span className="ms-2">Loading members...</span>
                                        </div>
                                    ) : departmentMembers.length > 0 ? (
                                        departmentMembers.map((member) => (
                                            <div key={member.user_id} className="d-flex align-items-center mb-3 p-2 border-bottom">
                                                <div className="me-3">
                                                    {member.photo ? (
                                                        <img 
                                                            src={member.photo} 
                                                            alt={member.display_name}
                                                            className="rounded-circle"
                                                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                             style={{ width: '40px', height: '40px', fontSize: '14px' }}>
                                                            {member.first_name ? member.first_name.charAt(0).toUpperCase() : '?'}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold">
                                                        {member.display_name}
                                                        {member.is_leader && (
                                                            <span className="badge bg-warning ms-2">Leader</span>
                                                        )}
                                                    </div>
                                                    <div className="text-muted small">@{member.username}</div>
                                                </div>
                                                <div>
                                                    {!member.is_leader && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeMemberFromDepartment(member)}
                                                            disabled={removingUsers.has(member.user_id)}
                                                        >
                                                            {removingUsers.has(member.user_id) ? (
                                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                            ) : (
                                                                'Remove'
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center text-muted py-3">
                                            <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                            <p>No members in this department</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Available Group Members */}
                            <div className="col-md-6">
                                <h6 className="text-primary">Available Members ({availableMembers.length})</h6>
                                <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {availableMembers.length > 0 ? (
                                        availableMembers.map((member) => (
                                            <div key={member.user_id} className="d-flex align-items-center mb-3 p-2 border-bottom">
                                                <div className="me-3">
                                                    <div className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                         style={{ width: '40px', height: '40px', fontSize: '14px' }}>
                                                        {member.first_name ? member.first_name.charAt(0).toUpperCase() : '?'}
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold">{member.first_name} {member.last_name}</div>
                                                    <div className="text-muted small">@{member.username}</div>
                                                </div>
                                                <div>
                                                    <button
                                                        className="btn btn-sm btn-outline-success"
                                                        onClick={() => addMemberToDepartment(member)}
                                                        disabled={addingUsers.has(member.user_id)}
                                                    >
                                                        {addingUsers.has(member.user_id) ? (
                                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        ) : (
                                                            'Add'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center text-muted py-3">
                                            <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                            <p>All group members are already in this department</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GroupChat;
